import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useBlocker, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useConfirm } from '../../components/ConfirmDialog';
import { plural } from '../../utils/format';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { usePermissions } from '../../context/PermissionsContext';
import { pagePathForHref } from './canvasBridge';
import { useReleaseStatus } from './useReleaseStatus';
import PageSidebar from './PageSidebar';
import EditorCanvas from './EditorCanvas';
import PageFieldsPanel from './PageFieldsPanel';
import SaveModal from './SaveModal';
import ReleaseBar from './ReleaseBar';
import DeployProdModal from './DeployProdModal';
import BulkReviewModal from './BulkReviewModal';
import SiteHealth from './SiteHealth';
import HistoryPanel, { VersionBanner } from './HistoryPanel';
import VersionModal from './VersionModal';

const HOME = 'site/index.html';
const SHARED_SCOPES = ['header', 'footer'];
// Matches the CSS that shows one pane at a time (Bootstrap's lg)
const BELOW_LG_QUERY = '(max-width: 991.98px)';

/** A shared header/footer as a page-shaped object, so the canvas and panel handle it unchanged. */
const sharedAsPage = (scope, dto) => ({
  path: dto.path,
  sha: dto.baseSha,
  url: `Shared ${scope} (every page)`,
  previewHtml: dto.previewHtml,
  units: dto.units,
  notices: dto.notices,
  shared: { scope, name: scope, variants: dto.variants, pagesWithout: dto.pagesWithout },
});

/**
 * Marketing site editor: pick a page, click text in the preview to edit it, save as a commit on
 * main (deploys to the test site), and deploy to production from the release bar.
 */
const MarketingSiteEditor = () => {
  const ensureToken = useAuthToken();
  const { canEdit, loading: permissionsLoading } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const sharedParam = searchParams.get('shared');
  const shared = SHARED_SCOPES.includes(sharedParam) ? sharedParam : null;
  const health = !shared && searchParams.get('view') === 'health';
  const healthTab = searchParams.get('tab') || 'seo';
  const selectedPath = searchParams.get('page') || HOME;

  const [info, setInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [pagesError, setPagesError] = useState(null);

  const [page, setPage] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [lint, setLint] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyReload, setHistoryReload] = useState(0);
  const [viewing, setViewing] = useState(null); // { entry, html } of a past version shown over the canvas
  const [versionModal, setVersionModal] = useState(null); // { mode: 'compare' | 'restore', entry }
  const [faqSyncOff, setFaqSyncOff] = useState(() => new Set()); // FAQ items whose structured data is left alone
  const [faqEdit, setFaqEdit] = useState(null); // { op, index, question, answer } under review
  const manualJsonLd = useRef(false);
  const [edits, setEdits] = useState(() => new Map());
  const [bridge, setBridge] = useState(null);
  const [activeUnitId, setActiveUnitId] = useState(null);
  const [showSave, setShowSave] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showDeploy, setShowDeploy] = useState(false);
  // An unsaved llms.txt draft under Site health counts as unsaved work too
  const [draftDirty, setDraftDirty] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const release = useReleaseStatus();
  const [indexStatus, setIndexStatus] = useState(null);
  // Below lg the three columns show one at a time, picked from the pane switcher
  const [pane, setPane] = useState('preview');
  // ...and Discard/Save move from the preview toolbar to a bar under every pane
  const belowLg = useMediaQuery(BELOW_LG_QUERY);
  const mainHead = release.status?.mainHead?.sha;
  const rootRef = useRef(null);
  const dirty = edits.size > 0;
  // Edits just committed (a dialog's "Saved" screen) are not unsaved; the page reloads when it closes
  const unsaved = (dirty && !saved) || draftDirty;

  // The admin navbar is sticky, not fixed: size the editor to the space below it.
  useLayoutEffect(() => {
    const measure = () => {
      const nav = document.querySelector('.navbar-custom');
      rootRef.current?.style.setProperty('--mse-nav-h', `${nav ? nav.offsetHeight : 57}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ------------------------------------------------------------------ loading

  useEffect(() => {
    (async () => {
      try {
        await ensureToken();
        const [infoRes, pagesRes] = await Promise.all([marketingSiteService.getInfo(), marketingSiteService.listPages()]);
        setInfo(infoRes.data);
        setPages(pagesRes.data);
      } catch (err) {
        setPagesError(apiErrorMessage(err, 'Could not load the page list.'));
      } finally {
        setPagesLoading(false);
      }
    })();
  }, [ensureToken]);

  useEffect(() => {
    if (health) return undefined;
    let cancelled = false;
    (async () => {
      setPageLoading(true);
      setPageError(null);
      try {
        await ensureToken();
        const response = shared
          ? await marketingSiteService.getChrome(shared)
          : await marketingSiteService.getPage(selectedPath);
        if (cancelled) return;
        setPage(shared ? sharedAsPage(shared, response.data) : response.data);
        setSaved(false);
        setViewing(null);
        setFaqSyncOff(new Set());
        manualJsonLd.current = false;
        setEdits(new Map());
        setActiveUnitId(null);
      } catch (err) {
        if (!cancelled) setPageError(apiErrorMessage(err, 'Could not load this page.'));
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPath, shared, health, reloadToken, ensureToken]);

  // Sitemap state for the release bar warning; rechecked whenever main moves (a save, a bulk change).
  useEffect(() => {
    if (!mainHead) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await ensureToken();
        const response = await marketingSiteService.indexStatus();
        if (!cancelled) setIndexStatus(response.data);
      } catch {
        // Only a warning chip: leave it off rather than show an error.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mainHead, ensureToken]);

  // SEO findings for the page as it is on main; loads after the page so it never delays editing.
  useEffect(() => {
    setLint(null);
    if (!page || page.shared || health) return undefined;
    let cancelled = false;
    setLint({ loading: true, findings: [] });
    (async () => {
      try {
        await ensureToken();
        // Either check may fail on its own; show what the other found.
        const [seo, links] = await Promise.allSettled([
          marketingSiteService.pageLint(page.path),
          marketingSiteService.pageLinks(page.path),
        ]);
        if (cancelled) return;
        if (seo.status === 'rejected' && links.status === 'rejected') throw seo.reason;
        // Shared header/footer links are checked under Site health, not repeated on every page.
        const linkFindings = links.status === 'fulfilled' ? links.value.data.findings.filter((f) => !f.inChrome) : [];
        const seoFindings = seo.status === 'fulfilled' ? seo.value.data.findings : [];
        setLint({ loading: false, findings: [...seoFindings, ...linkFindings] });
      } catch (err) {
        if (!cancelled) setLint({ loading: false, findings: [], error: apiErrorMessage(err, 'the check failed') });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, health, ensureToken]);

  const reloadPage = useCallback(() => {
    setShowSave(false);
    setReloadToken((n) => n + 1);
  }, []);

  // ------------------------------------------------------------------ edits

  const unitsById = useMemo(() => new Map((page?.units || []).map((u) => [u.id, u])), [page]);

  const originalOf = useCallback(
    (unit) => (bridge?.has(unit.id) ? bridge.original(unit.id) : unit.value),
    [bridge]
  );

  /** From the canvas: value null means the unit is back to its original. */
  const handleCanvasChange = useCallback((id, value) => {
    setEdits((prev) => {
      if (value === null ? !prev.has(id) : prev.get(id) === value) return prev;
      const next = new Map(prev);
      if (value === null) next.delete(id);
      else next.set(id, value);
      return next;
    });
  }, []);

  /** From the side panel. Canvas units go through the bridge so the preview stays in sync. */
  const handlePanelEdit = useCallback(
    (unit, value) => {
      if (unit.id === page?.faq?.jsonLdUnitId) manualJsonLd.current = true; // hand edits win over FAQ sync
      if (bridge?.has(unit.id)) {
        bridge.setUnitValue(unit.id, value);
        return;
      }
      handleCanvasChange(unit.id, value === unit.value ? null : value);
    },
    [bridge, handleCanvasChange, page]
  );

  const handleRevert = useCallback(
    (unit) => {
      if (bridge?.has(unit.id)) bridge.revert(unit.id);
      else handleCanvasChange(unit.id, null);
    },
    [bridge, handleCanvasChange]
  );

  const editList = useMemo(
    () =>
      [...edits.entries()]
        .filter(([id]) => unitsById.has(id))
        .map(([id, value]) => {
          const u = unitsById.get(id);
          return {
            id,
            value,
            start: u.start,
            end: u.end,
            kind: u.kind,
            label: u.group === 'body' ? `<${u.tag}> ${u.label}` : u.label,
            original: originalOf(u),
          };
        }),
    [edits, unitsById, originalOf]
  );

  // FAQ edits carry into the FAQPage structured data (server-side string patch, debounced). The key
  // holds only the FAQ texts and sync choices, so the structured-data edit this sets never re-triggers it.
  const faqSyncKey = useMemo(() => {
    const faq = page?.faq;
    if (!faq?.jsonLdUnitId) return '';
    return JSON.stringify(
      faq.items
        .filter((i) => i.jsonIndex >= 0 && !faqSyncOff.has(i.index))
        .map((i) => [i.jsonIndex, edits.get(i.questionUnitId) ?? null, edits.get(i.answerUnitId) ?? null])
        .filter(([, q, a]) => q !== null || a !== null)
    );
  }, [page, edits, faqSyncOff]);

  useEffect(() => {
    const faq = page?.faq;
    const jsonUnit = faq?.jsonLdUnitId && unitsById.get(faq.jsonLdUnitId);
    if (!jsonUnit || manualJsonLd.current) return undefined;
    const items = JSON.parse(faqSyncKey || '[]').map(([jsonIndex, question, answer]) => ({
      jsonIndex,
      question: question ?? undefined,
      answer: answer ?? undefined,
    }));
    if (items.length === 0) {
      handleCanvasChange(jsonUnit.id, null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await ensureToken();
        const { json } = (await marketingSiteService.faqSync({ json: jsonUnit.value, items })).data;
        if (!cancelled && !manualJsonLd.current) handleCanvasChange(jsonUnit.id, json === jsonUnit.value ? null : json);
      } catch {
        // Leave the structured data as it is; the save dialog shows exactly what changes.
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // faqSyncKey carries every input that matters.
  }, [faqSyncKey]);

  const faqProps = useMemo(
    () => ({
      dirty,
      syncOff: faqSyncOff,
      onToggleSync: (index) =>
        setFaqSyncOff((prev) => {
          const next = new Set(prev);
          if (next.has(index)) next.delete(index);
          else next.add(index);
          return next;
        }),
      onStructure: (params) => setFaqEdit(params),
    }),
    [dirty, faqSyncOff]
  );
  const faqEditParams = useMemo(() => {
    if (!faqEdit || !page) return null;
    const { label, ...params } = faqEdit; // the label names the question in the dialog only
    return { ...params, path: page.path, sha: page.sha };
  }, [faqEdit, page]);
  const loadFaqPreview = useCallback(async () => (await marketingSiteService.faqEditPreview(faqEditParams)).data, [faqEditParams]);
  const applyFaqEdit = useCallback(
    async ({ baseSha, message }) =>
      (await marketingSiteService.faqEditApply({ params: faqEditParams, baseSha, message })).data,
    [faqEditParams]
  );

  // ------------------------------------------------------------------ navigation and guards

  const discardOptions = {
    title: 'Discard unsaved changes?',
    body: dirty
      ? `${plural(edits.size, 'change')} on this page ${edits.size === 1 ? 'is' : 'are'} not saved and will be lost.`
      : 'Your llms.txt edits are not saved and will be lost.',
    confirmLabel: 'Discard changes',
    cancelLabel: 'Keep editing',
    variant: 'danger',
  };
  const discardOptionsRef = useRef(discardOptions);
  discardOptionsRef.current = discardOptions;

  /** For actions that drop edits without navigating (Discard, Reload). */
  const confirmDiscard = useCallback(async () => !dirty || confirm(discardOptionsRef.current), [dirty, confirm]);

  // Every navigation away from unsaved work asks first: the sidebar, followed links, Site health tabs,
  // the navbar, the AI assistant link and browser Back. Read through a ref so the check is never stale.
  const unsavedRef = useRef(false);
  unsavedRef.current = unsaved;
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      unsavedRef.current &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
    []
  );
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== 'blocked') return undefined;
    let active = true;
    confirm(discardOptionsRef.current).then((ok) => {
      if (!active) return;
      if (ok) {
        // Dropped now: Site health does not reload the page, and the editor reloads it when reopened
        unsavedRef.current = false;
        setEdits(new Map());
        setDraftDirty(false);
        setShowSave(false);
        setFaqEdit(null);
        setVersionModal(null);
        setShowDeploy(false);
        blocker.proceed();
      } else {
        blocker.reset();
      }
    });
    return () => {
      active = false;
    };
    // A new blocked navigation (another Back) asks again.
  }, [blocker.state, blocker.location]);

  const selectPage = useCallback(
    (path) => {
      if (path === selectedPath && !shared && !health) return;
      setSearchParams({ page: path });
    },
    [selectedPath, shared, health, setSearchParams]
  );

  const selectHealth = useCallback(
    (tab = 'seo') => setSearchParams({ view: 'health', tab, page: selectedPath }),
    [selectedPath, setSearchParams]
  );

  const selectShared = useCallback(
    (scope) => {
      if (scope === shared) return;
      setSearchParams({ shared: scope });
    },
    [shared, setSearchParams]
  );

  const handleNavigate = useCallback(
    (href) => {
      // Shared mode edits the header/footer only; the dimmed page around it is not a way out.
      if (shared || viewing) return;
      const target = pagePathForHref(href);
      if (target && pages.some((p) => p.path === target)) {
        selectPage(target);
      } else if (/^https?:\/\//i.test(href)) {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else if (href?.startsWith('/') && info?.siteUrl) {
        window.open(info.siteUrl.replace(/\/$/, '') + href, '_blank', 'noopener,noreferrer');
      }
    },
    [shared, viewing, pages, selectPage, info]
  );

  const viewVersion = useCallback(
    async (entry) => {
      try {
        await ensureToken();
        const response = await marketingSiteService.pageVersion(page.path, entry.sha, 'TEXT');
        setViewing({ entry, html: response.data.previewHtml });
      } catch (err) {
        setPageError(apiErrorMessage(err, 'Could not load that version.'));
      }
    },
    [ensureToken, page]
  );

  const versionView = useMemo(
    () =>
      viewing && {
        html: viewing.html,
        banner: (
          <VersionBanner
            entry={viewing.entry}
            canRestore={canEdit && !dirty}
            onBack={() => setViewing(null)}
            onCompare={() => setVersionModal({ mode: 'compare', entry: viewing.entry })}
            onRestore={() => setVersionModal({ mode: 'restore', entry: viewing.entry })}
          />
        ),
      },
    [viewing, canEdit, dirty]
  );

  // Closing or reloading the browser tab
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  const dialogOpen = showSave || !!faqEdit || !!versionModal || showDeploy;
  const openSave = useCallback(() => {
    // Cmd+S on an open dialog (the "Saved" screen included) must not reopen it over stale state
    if (dialogOpen || document.body.classList.contains('modal-open')) return;
    bridge?.blur();
    if (canEdit && edits.size > 0) {
      setSaved(false);
      setShowSave(true);
    }
  }, [dialogOpen, bridge, canEdit, edits.size]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        openSave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openSave]);

  const chromeParams = useMemo(
    () =>
      shared && {
        scope: shared.toUpperCase(),
        edits: editList.map(({ id, value, start, end }) => ({ id, value, start, end })),
      },
    [shared, editList]
  );
  const loadChromePreview = useCallback(
    async () => (await marketingSiteService.chromePreview(chromeParams)).data,
    [chromeParams]
  );
  const applyChrome = useCallback(
    async ({ baseSha, message }) => (await marketingSiteService.chromeApply({ params: chromeParams, baseSha, message })).data,
    [chromeParams]
  );

  // Memoised: a new object would give the canvas a fresh iframe on every render.
  const canvasPage = useMemo(
    () => (page?.shared ? { ...page, testUrl: info?.siteUrl, prodUrl: info?.prodUrl } : page),
    [page, info]
  );

  const discard = async () => {
    if (await confirm(discardOptionsRef.current)) reloadPage();
  };

  const closeSave = () => {
    setShowSave(false);
    if (saved) reloadPage(); // fresh version and units after a commit
  };

  // ------------------------------------------------------------------ render

  if (info && !info.githubConfigured) {
    return (
      <div className="container py-4">
        <Alert variant="warning">
          The marketing site editor needs a GitHub token. Set <code>MARKETING_SITE_GITHUB_TOKEN</code> on this
          environment and restart the admin app.
        </Alert>
      </div>
    );
  }

  const changeCount = `${edits.size} change${edits.size === 1 ? '' : 's'}`;
  const actions = (
    <>
      {!permissionsLoading && !canEdit && (
        <span className="mse-viewonly">
          <Eye size={14} /> View only
        </span>
      )}
      {canEdit && dirty && !belowLg && (
        <Button size="sm" variant="outline-secondary" onClick={discard}>
          Discard
        </Button>
      )}
      {canEdit && !belowLg && (
        <Button size="sm" variant="primary" onClick={openSave} disabled={!dirty} title="Save (Cmd+S)">
          Save{dirty ? ` ${changeCount}` : ''}
        </Button>
      )}
    </>
  );

  const showHistory = historyOpen && page && !page.shared;
  const openHistory = () => {
    setHistoryOpen(true);
    setPane('panel');
  };
  // Whether this page differs from production, from the files a deploy would ship. Unknown (no chip)
  // when production has commits of its own or GitHub's 300-file list is full.
  const changedFiles = release.status?.changedFiles;
  let prodState = null;
  if (page && !page.shared && Array.isArray(changedFiles) && !(release.status.behindBy > 0)) {
    if (changedFiles.some((f) => f.path === page.path)) prodState = 'pending';
    else if (changedFiles.length < 300) prodState = 'same';
  }
  const panes = [
    ['pages', 'Pages'],
    ['preview', health ? 'Site health' : 'Preview'],
    ...(health ? [] : [['panel', showHistory ? 'History' : `Edit${dirty ? ` (${edits.size})` : ''}`]]),
  ];
  const shownPane = health && pane === 'panel' ? 'preview' : pane;
  const canvasShown = !belowLg || shownPane === 'preview';
  /** Runs fn against the preview, first switching to its pane when the panel is the one showing. */
  const inCanvas = (fn) => {
    if (canvasShown) {
      fn();
      return;
    }
    setPane('preview');
    setTimeout(fn, 0); // after the pane switch renders, so the frame has a layout to scroll
  };

  return (
    <div className="mse-root" ref={rootRef}>
      <ReleaseBar
        status={release.status}
        error={release.error}
        canEdit={canEdit}
        onDeployClick={() => setShowDeploy(true)}
        sitemap={indexStatus?.sitemap}
        onSitemapClick={() => selectHealth('index')}
      />
      {info?.githubAccess && !info.githubAccess.ok && (
        <Alert variant="danger" className="mse-page-error">
          <strong>GitHub access problem.</strong> {info.githubAccess.message}
          {info.githubAccess.tokenType && info.githubAccess.tokenType !== 'none' && (
            <span className="mse-hint"> (token type: {info.githubAccess.tokenType})</span>
          )}
        </Alert>
      )}
      {pageError && (
        <Alert variant="danger" className="mse-page-error" onClose={() => setPageError(null)} dismissible>
          {pageError}
        </Alert>
      )}
      <nav className="mse-panes d-lg-none" aria-label="Editor panes">
        {panes.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`mse-pane-tab${shownPane === key ? ' active' : ''}`}
            aria-pressed={shownPane === key}
            onClick={() => setPane(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className={`mse-body mse-pane-${shownPane}`}>
        <PageSidebar
          pages={pages}
          loading={pagesLoading}
          error={pagesError}
          selectedPath={selectedPath}
          shared={shared}
          health={health}
          dirty={dirty}
          onSelect={(path) => {
            selectPage(path);
            setPane('preview');
          }}
          onSelectShared={(scope) => {
            selectShared(scope);
            setPane('preview');
          }}
          onSelectHealth={() => {
            selectHealth();
            setPane('preview');
          }}
        />
        {health ? (
          <SiteHealth
            tab={healthTab}
            onTab={selectHealth}
            canEdit={canEdit}
            siteUrl={info?.siteUrl}
            onOpenPage={selectPage}
            onOpenShared={() => selectShared('footer')}
            onIndexStatus={setIndexStatus}
            onSaved={release.refresh}
            onDraftChange={setDraftDirty}
          />
        ) : (
          <>
            <EditorCanvas
              page={canvasPage}
              loading={pageLoading}
              canEdit={canEdit}
              actions={actions}
              allowNavigate={!shared && !viewing}
              historyOpen={historyOpen}
              version={versionView}
              prodState={prodState}
              onOpenHistory={openHistory}
              onHistory={() => {
                if (!historyOpen) setPane('panel');
                setHistoryOpen((v) => !v);
              }}
              onBridge={setBridge}
              onSelect={setActiveUnitId}
              onChange={handleCanvasChange}
              onNavigate={handleNavigate}
              onSaveShortcut={openSave}
              onReload={async () => {
                if (await confirmDiscard()) reloadPage();
              }}
            />
            {showHistory ? (
              <HistoryPanel
                page={page}
                canEdit={canEdit}
                dirty={dirty}
                viewingRef={viewing?.entry.sha}
                reloadToken={historyReload}
                onView={(entry) => {
                  viewVersion(entry);
                  setPane('preview');
                }}
                onCompare={(entry) => setVersionModal({ mode: 'compare', entry })}
                onRestore={(entry) => setVersionModal({ mode: 'restore', entry })}
                onClose={() => {
                  setHistoryOpen(false);
                  setViewing(null);
                }}
              />
            ) : (
              <PageFieldsPanel
                page={page}
                edits={edits}
                originalOf={originalOf}
                canEdit={canEdit}
                bridge={bridge}
                activeUnitId={activeUnitId}
                onEdit={handlePanelEdit}
                onRevert={handleRevert}
                onHighlight={(id) => inCanvas(() => bridge?.highlight(id))}
                onOpenPage={selectPage}
                lint={lint}
                faqProps={faqProps}
                canvasShown={canvasShown}
                inCanvas={inCanvas}
              />
            )}
          </>
        )}
      </div>
      {belowLg && canEdit && dirty && (
        <div className="mse-actionbar">
          <span className="mse-hint">{changeCount} not saved</span>
          <Button size="sm" variant="outline-secondary" onClick={discard}>
            Discard
          </Button>
          <Button size="sm" variant="primary" onClick={openSave}>
            Save
          </Button>
        </div>
      )}
      {!health && page && !page.shared && showSave && (
        <SaveModal
          show={showSave}
          page={page}
          editList={editList}
          onClose={closeSave}
          onSaved={() => {
            setSaved(true);
            release.refresh();
          }}
          onReloadPage={reloadPage}
        />
      )}
      {!health && page?.shared && showSave && (
        <BulkReviewModal
          show={showSave}
          title={`Save the shared ${page.shared.name}`}
          description={`Changes the same ${page.shared.name} text on every page that has it, in one commit to the test site.`}
          applyLabel="Save to test site"
          siteUrl={info?.siteUrl}
          loadPreview={loadChromePreview}
          apply={applyChrome}
          onClose={closeSave}
          onApplied={() => {
            setSaved(true);
            release.refresh();
          }}
        />
      )}
      {faqEdit && page && (
        <BulkReviewModal
          show={!!faqEdit}
          title={
            { ADD: 'Add a FAQ question', REMOVE: 'Remove a FAQ question', ADD_JSONLD: 'Add FAQ structured data', ADD_TO_JSONLD: 'Add to the structured data' }[
              faqEdit.op
            ]
          }
          description={
            faqEdit.op === 'REMOVE'
              ? `Removes question ${faqEdit.index + 1}${faqEdit.label ? `, "${faqEdit.label}",` : ''} from the page and its FAQPage structured data, in one commit to the test site.`
              : 'Changes the page and its FAQPage structured data together, in one commit to the test site.'
          }
          applyLabel={faqEdit.op === 'REMOVE' ? 'Remove question' : 'Save to test site'}
          showCount={faqEdit.op !== 'REMOVE'}
          siteUrl={info?.siteUrl}
          loadPreview={loadFaqPreview}
          apply={applyFaqEdit}
          onClose={() => {
            const applied = saved;
            setFaqEdit(null);
            if (applied) {
              setSaved(false);
              reloadPage();
            }
          }}
          onApplied={() => {
            setSaved(true);
            release.refresh();
          }}
        />
      )}
      {versionModal && page && (
        <VersionModal
          show={!!versionModal}
          mode={versionModal.mode}
          page={page}
          entry={versionModal.entry}
          onRestored={() => {
            setSaved(true);
            release.refresh();
          }}
          onClose={() => {
            const restored = saved && versionModal.mode === 'restore';
            setVersionModal(null);
            if (restored) {
              setSaved(false);
              setViewing(null);
              setHistoryReload((n) => n + 1);
              setReloadToken((n) => n + 1);
            }
          }}
        />
      )}
      {showDeploy && (
        <DeployProdModal
          show={showDeploy}
          status={release.status}
          sitemap={indexStatus?.sitemap}
          onClose={() => {
            setShowDeploy(false);
            release.refresh();
          }}
          onDeployed={() => release.refresh()}
          onRefresh={release.refresh}
          onOpenHealth={(tab) => {
            setShowDeploy(false);
            selectHealth(tab);
            setPane('preview');
          }}
        />
      )}
      {confirmDialog}
    </div>
  );
};

export default MarketingSiteEditor;
